// src/features/toolkit/multiSession.ts
// Multi-session support for prompt techniques: fetch selected sessions'
// summaries and stitch them into one combined text for a single LLM run.
// run_cluster uses a caller-provided `summary` verbatim, so this stays
// entirely in the frontend.
import { trimTrailingSlash } from "../../lib/url";

export type SessionSummaryEntry = {
  session_id: string;
  session_date?: string;
  summary?: string;
};

// session_date can be absent on old records — same fallback the pickers use.
export function sessionLabel(e: Pick<SessionSummaryEntry, "session_id" | "session_date">): string {
  return e.session_date || e.session_id;
}

export class MissingSummariesError extends Error {
  labels: string[];
  constructor(labels: string[]) {
    super(`Missing summaries: ${labels.join(", ")}`);
    this.name = "MissingSummariesError";
    this.labels = labels;
  }
}

// Input in list order (newest first); output stitches newest-LAST so the
// most recent session sits closest to the model's answer. Heading style
// mirrors run_cluster's own "## Ülés" section labels.
export function combineSummaries(entries: SessionSummaryEntry[]): string {
  const missing = entries.filter((e) => !(e.summary || "").trim());
  if (missing.length) throw new MissingSummariesError(missing.map(sessionLabel));
  return [...entries]
    .reverse()
    .map((e) => `## Ülés — ${sessionLabel(e)}\n${(e.summary as string).trim()}`)
    .join("\n\n");
}

export async function fetchSessionSummaries(
  apiBaseUrl: string,
  getToken: () => Promise<string | null>,
  sessionIds: string[]
): Promise<SessionSummaryEntry[]> {
  const base = trimTrailingSlash(apiBaseUrl);
  const token = await getToken();
  if (!token) throw new Error("Not authenticated");
  return Promise.all(
    sessionIds.map(async (id) => {
      const resp = await fetch(`${base}/sessions/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(`GET /sessions/${id} failed ${resp.status}${text ? ` – ${text}` : ""}`);
      }
      const data = await resp.json();
      const item = data?.item || data; // same shape tolerance as useSessionData
      return {
        session_id: String(item?.session_id || id),
        session_date: item?.session_date || undefined,
        summary: item?.summary || undefined,
      };
    })
  );
}
